import { exists } from '../helpers';

type WithId = { _id: string };
export type Flat<T extends WithId> = T & { children: string[] };
export type Nested<T extends WithId> = T & { children: Nested<T>[] };

/**
 * Converts a list of flat objects (children = id list) to nested
 * objects (children = reference).  Returns a list of the new root
 * objects.
 **/
export function toNested<T extends WithId>(
  nodes: readonly Flat<T>[],
): Nested<T>[] {
  const childrenIds = new Set(nodes.flatMap((x) => x.children));
  // This is slow if there's multiple roots.
  // However, I've never seen a Webflow component with multiple roots.
  return nodes
    .filter((x) => !childrenIds.has(x._id))
    .map((n) => extractTreeById(nodes, n._id));
}

export function extractTreeById<T extends WithId>(
  nodes: readonly Flat<T>[],
  rootId: string,
): Nested<T> {
  const byId = new Map(nodes.map((x) => [x._id, x]));

  function recreate(node: Flat<T>): Nested<T> {
    return {
      ...node,
      children: (node.children || [])
        .map((x) => byId.get(x))
        .filter(exists)
        .map(recreate),
    };
  }

  const root = byId.get(rootId);
  if (!root) throw new Error(`Missing root node ${rootId}`);
  return recreate(root);
}

export function fromNested<T extends WithId>(
  nodes: readonly Nested<T>[],
): Flat<T>[] {
  return nodes.flatMap((x) => {
    const children = x.children.map((y) => y._id);
    return [{ ...x, children }, ...fromNested(x.children)];
  });
}
